import type { ProgramSlideTextBoxV1, ProgramSlideTextRunV1 } from '../types/programContentSlideCanvas';

const HEX6 = /^#[0-9A-Fa-f]{6}$/;

export function normalizeSlideRunHex(raw: unknown): string {
  const s = String(raw ?? '').trim();
  if (HEX6.test(s)) return s.toLowerCase();
  return '#141414';
}

export function getSlideTextBoxPlainText(box: ProgramSlideTextBoxV1): string {
  if (box.textRuns && box.textRuns.length > 0) {
    return box.textRuns.map((r) => r.text).join('');
  }
  return box.text ?? '';
}

export function plainTextFromRuns(runs: ProgramSlideTextRunV1[]): string {
  return runs.map((r) => r.text).join('');
}

/** 인접·동일 해석 색이면 병합. 빈 런 제거. */
export function mergeAdjacentTextRuns(
  runs: ProgramSlideTextRunV1[],
  defaultColorHex: string
): ProgramSlideTextRunV1[] {
  const def = normalizeSlideRunHex(defaultColorHex);
  const out: ProgramSlideTextRunV1[] = [];
  for (const r of runs) {
    const t = r.text ?? '';
    if (!t) continue;
    const resolved = normalizeSlideRunHex(r.colorHex ?? def);
    const last = out[out.length - 1];
    const lastResolved = last ? normalizeSlideRunHex(last.colorHex ?? def) : null;
    if (last && lastResolved === resolved) {
      last.text += t;
    } else {
      out.push({ text: t, colorHex: resolved === def ? undefined : resolved });
    }
  }
  return out;
}

/**
 * 저장용: text 동기화, 단일 기본색 런이면 textRuns 생략.
 */
export function compactSlideTextBoxContent(
  box: Pick<ProgramSlideTextBoxV1, 'text' | 'textRuns' | 'colorHex'>
): Pick<ProgramSlideTextBoxV1, 'text' | 'textRuns'> {
  const def = normalizeSlideRunHex(box.colorHex);
  if (!box.textRuns?.length) {
    return { text: box.text ?? '', textRuns: undefined };
  }
  const runs = mergeAdjacentTextRuns(box.textRuns, def);
  const text = plainTextFromRuns(runs);
  if (!text) {
    return { text: '', textRuns: undefined };
  }
  if (runs.length === 1 && runs[0].colorHex === undefined) {
    return { text, textRuns: undefined };
  }
  return { text, textRuns: runs };
}

export function runsToEditableHtml(
  runs: ProgramSlideTextRunV1[] | undefined,
  plainFallback: string,
  defaultColorHex: string,
  mapDisplayColor?: (hex: string) => string
): string {
  const def = normalizeSlideRunHex(defaultColorHex);
  const list =
    runs && runs.length > 0
      ? mergeAdjacentTextRuns(runs, def)
      : plainFallback
      ? [{ text: plainFallback, colorHex: undefined as string | undefined }]
      : [];
  if (!list.length) return '<br />';
  return list
    .map((r) => {
      const base = normalizeSlideRunHex(r.colorHex ?? def);
      const c = mapDisplayColor ? mapDisplayColor(base) : base;
      const body = escapeHtml(r.text).replace(/\n/g, '<br />');
      return `<span style="color:${escapeAttr(c)}">${body}</span>`;
    })
    .join('');
}

function isBlockLikeElement(el: HTMLElement): boolean {
  const tag = el.tagName;
  return tag === 'DIV' || tag === 'P' || tag === 'LI';
}

function needsBlockSeparatorBetween(a: Node, b: Node): boolean {
  if (a.nodeType === Node.ELEMENT_NODE && (a as HTMLElement).tagName === 'BR') return false;
  if (b.nodeType === Node.ELEMENT_NODE && (b as HTMLElement).tagName === 'BR') return false;
  return (
    a.nodeType === Node.ELEMENT_NODE &&
    b.nodeType === Node.ELEMENT_NODE &&
    isBlockLikeElement(a as HTMLElement) &&
    isBlockLikeElement(b as HTMLElement)
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/\n/g, '&#10;');
}

/** contenteditable 루트에서 평문 + 런 추출 */
export function parseRunsFromContentEditableRoot(
  root: HTMLElement,
  defaultColorHex: string
): Pick<ProgramSlideTextBoxV1, 'text' | 'textRuns'> {
  const def = normalizeSlideRunHex(defaultColorHex);
  const chunks: { text: string; hex: string }[] = [];

  const push = (t: string, hex: string) => {
    if (!t) return;
    const h = normalizeSlideRunHex(hex);
    const last = chunks[chunks.length - 1];
    if (last && last.hex === h) last.text += t;
    else chunks.push({ text: t, hex: h });
  };

  const walk = (node: Node, inheritedHex: string) => {
    if (node.nodeType === Node.TEXT_NODE) {
      push(node.textContent ?? '', inheritedHex);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    const tag = el.tagName;
    if (tag === 'BR') {
      push('\n', inheritedHex);
      return;
    }
    let nextHex = inheritedHex;
    if (tag === 'SPAN' || tag === 'FONT') {
      const raw = el.style?.color;
      const fromStyle = raw ? cssColorToHex(raw) : '';
      if (fromStyle) nextHex = fromStyle;
    }
    walkElementChildren(el, nextHex);
  };

  const walkElementChildren = (parent: HTMLElement, inheritedHex: string) => {
    const nodes = Array.from(parent.childNodes);
    for (let i = 0; i < nodes.length; i += 1) {
      const node = nodes[i]!;
      if (i > 0 && needsBlockSeparatorBetween(nodes[i - 1]!, node)) {
        push('\n', inheritedHex);
      }
      walk(node, inheritedHex);
    }
  };

  walkElementChildren(root, def);
  const segments: ProgramSlideTextRunV1[] = chunks.map((ch) => ({
    text: ch.text,
    colorHex: ch.hex === def ? undefined : ch.hex,
  }));
  const merged = mergeAdjacentTextRuns(segments, def);
  const text = plainTextFromRuns(merged);
  return compactSlideTextBoxContent({ text, textRuns: merged, colorHex: def });
}

/** #rgb / rgb() / rgba() → #rrggbb (알파 0이면 null) */
/** contenteditable 루트 기준 평문 오프셋(선택·커서). */
export function getPlainTextOffsetsFromSelection(
  root: HTMLElement,
  selection: Selection
): { start: number; end: number } | null {
  if (!selection.rangeCount) return null;
  const an = selection.anchorNode;
  const fn = selection.focusNode;
  if (!an || !fn) return null;
  if (!root.contains(an) || !root.contains(fn)) return null;
  const rA = document.createRange();
  rA.selectNodeContents(root);
  rA.setEnd(an, selection.anchorOffset);
  const a = rA.toString().length;
  const rF = document.createRange();
  rF.selectNodeContents(root);
  rF.setEnd(fn, selection.focusOffset);
  const b = rF.toString().length;
  return { start: Math.min(a, b), end: Math.max(a, b) };
}

export function cssColorToHex(css: string): string | null {
  const s = css.trim();
  if (HEX6.test(s)) return s.toLowerCase();
  const m = s.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)$/i);
  if (!m) return null;
  const r = Math.min(255, Math.max(0, Number(m[1])));
  const g = Math.min(255, Math.max(0, Number(m[2])));
  const b = Math.min(255, Math.max(0, Number(m[3])));
  if (m[4] !== undefined && Number(m[4]) < 0.01) return null;
  return `#${byteHex(r)}${byteHex(g)}${byteHex(b)}`;
}

function byteHex(n: number): string {
  return n.toString(16).padStart(2, '0');
}

export function expandRunColorsToChars(
  text: string,
  runs: ProgramSlideTextRunV1[] | undefined,
  defaultColorHex: string
): string[] {
  const def = normalizeSlideRunHex(defaultColorHex);
  const colors: string[] = new Array(Math.max(0, text.length)).fill(def);
  if (!runs?.length) return colors;
  let i = 0;
  for (const r of runs) {
    const chunk = r.text ?? '';
    const hex = normalizeSlideRunHex(r.colorHex ?? def);
    for (let j = 0; j < chunk.length && i < text.length; j++, i++) {
      colors[i] = hex;
    }
  }
  return colors;
}

export function buildRunsFromChars(text: string, charColors: string[], defaultColorHex: string): ProgramSlideTextRunV1[] {
  const def = normalizeSlideRunHex(defaultColorHex);
  const runs: ProgramSlideTextRunV1[] = [];
  if (!text.length) return runs;
  let i = 0;
  while (i < text.length) {
    const h = normalizeSlideRunHex(charColors[i] ?? def);
    let j = i + 1;
    while (j < text.length && normalizeSlideRunHex(charColors[j] ?? def) === h) j++;
    const slice = text.slice(i, j);
    runs.push({ text: slice, colorHex: h === def ? undefined : h });
    i = j;
  }
  return mergeAdjacentTextRuns(runs, def);
}

export function applyColorToPlainRange(
  box: Pick<ProgramSlideTextBoxV1, 'text' | 'textRuns' | 'colorHex'>,
  start: number,
  end: number,
  newColorHex: string
): Pick<ProgramSlideTextBoxV1, 'text' | 'textRuns'> {
  const text = box.text ?? '';
  const def = normalizeSlideRunHex(box.colorHex);
  const nc = normalizeSlideRunHex(newColorHex);
  const s = Math.max(0, Math.min(start, end, text.length));
  const e = Math.max(0, Math.max(start, end, s), Math.min(text.length, Math.max(start, end)));
  const colors = expandRunColorsToChars(text, box.textRuns, def);
  for (let i = s; i < e; i++) colors[i] = nc;
  const runs = buildRunsFromChars(text, colors, def);
  return compactSlideTextBoxContent({ text, textRuns: runs, colorHex: def });
}

/** parseSlideLayoutJson 전용: 원시 JSON에서 textRuns 정규화 */
export function parseTextRunsFromJson(
  rawRuns: unknown,
  defaultColorHex: string,
  fallbackText: string
): ProgramSlideTextRunV1[] | undefined {
  if (!Array.isArray(rawRuns) || rawRuns.length === 0) return undefined;
  const def = normalizeSlideRunHex(defaultColorHex);
  const runs: ProgramSlideTextRunV1[] = [];
  for (const item of rawRuns) {
    if (!item || typeof item !== 'object') continue;
    const t = String((item as { text?: unknown }).text ?? '');
    if (!t) continue;
    const cRaw = (item as { colorHex?: unknown }).colorHex;
    const c =
      cRaw === undefined || cRaw === null || cRaw === ''
        ? undefined
        : normalizeSlideRunHex(cRaw);
    runs.push({
      text: t,
      colorHex: c === def ? undefined : c,
    });
  }
  const merged = mergeAdjacentTextRuns(runs, def);
  const joined = plainTextFromRuns(merged);
  if (joined !== fallbackText) return undefined;
  if (merged.length === 1 && merged[0].colorHex === undefined) return undefined;
  return merged.length ? merged : undefined;
}
